import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useRef } from 'react'
import { ResolvedOnDemandCity } from 'src/constants/onDemandCoverage'
import { numberWithCommas } from 'src/utils'
import { DirectoryCategory } from 'src/utils/directory'
import {
  buildOnDemandHref,
  getOnDemandImage,
  resolveOnDemandCity,
  resolveOnDemandCityByCoords
} from 'src/utils/onDemand'
import {
  onDemandCardClick,
  onDemandCardView
} from 'src/utils/pixels/spotmeEvents'

/**
 * Pure gating logic for the ficha on-demand cross-sell. Exposed for tests;
 * the component reads its own props and forwards them here.
 *
 * The card renders only when ALL of:
 *  - the facility is a STORAGE row — almacenamiento a domicilio stores boxes,
 *    so it is meaningless next to a parking facility whatever the market;
 *  - the facility resolves to a covered on-demand market — by its own
 *    COORDINATES first (`resolveOnDemandCityByCoords`), falling back to the
 *    hub-city slug only when the row was imported without a point. Coords are
 *    the FINAL word, not the preferred one: a point outside every service
 *    area means "not covered", never "ask the slug instead" — same semantics
 *    as `shouldShowOnDemandSearchCard` after the 2026-08-24 stale-slug fix;
 *  - that market has `partnerActive: true`.
 *
 * Scope note (Jules, 2026-08-24): this cross-sell exists ONLY on directory
 * fichas. Host listing pages — Monthly or Traditional, active or unavailable
 * — deliberately do NOT carry it: a host pays commission partly for exposure,
 * and their own listing page advertising a competing service is a
 * host-relations cost this surface doesn't have (a directory facility is not
 * a SpotMe customer). Revisit only with `directory_ficha` surface data in
 * hand.
 */
export function resolveFichaOnDemand(
  category: DirectoryCategory,
  citySlug: string | undefined,
  coordinates: { lat: number; lng: number } | null | undefined
): ResolvedOnDemandCity | null {
  if (category !== DirectoryCategory.Storage) return null

  const resolved = coordinates
    ? resolveOnDemandCityByCoords(coordinates.lat, coordinates.lng)
    : resolveOnDemandCity(citySlug)
  if (!resolved || !resolved.partnerActive) return null

  return resolved
}

interface OnDemandFichaCardProps {
  /** Resolved market from {@link resolveFichaOnDemand} — the parent gates on
   *  a non-null resolution, so the card itself never re-checks coverage. */
  resolved: ResolvedOnDemandCity
}

/**
 * On-demand cross-sell card in the ficha's right rail, directly below the
 * "verified nearby" card — the rail is the renter's recovery zone, and this
 * is its second exit ramp: the renter came for storage in a covered metro but
 * may prefer not to move anything themselves.
 *
 * Visual contract: follows the RAIL's vocabulary (rounded-2xl, hairline
 * border, cream-300 ground, blue-800 pill CTA matching `FICHA.NEARBY_CTA`),
 * not the search-card's — the two card families live on different pages and
 * each page keeps one voice. Photo on top via `getOnDemandImage` (the
 * "human + boxes + van" brief for off-landing card surfaces).
 *
 * Analytics: `storage_on_demand_card_view` (IntersectionObserver-gated, 50%
 * visible, session-deduped in the helper) + `storage_on_demand_card_click`,
 * both with `surface: 'directory_ficha'`. Discovery events — never rolled up.
 *
 * The href is `buildOnDemandHref(city)` WITHOUT `searchReturn`: that param
 * renders the landing's "Volver a tus resultados" affordance, which is
 * search-drawer copy — from a ficha, plain browser back is the right return.
 */
const OnDemandFichaCard = ({ resolved }: OnDemandFichaCardProps) => {
  const { t } = useTranslation('directorio')
  const ref = useRef<HTMLElement>(null)
  const { city, config } = resolved

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        onDemandCardView({ city, surface: 'directory_ficha' })
        observer.disconnect()
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [city])

  return (
    <section
      ref={ref}
      data-testid="on-demand-ficha-card"
      className="overflow-hidden rounded-2xl border border-ink-100/15 bg-cream-300"
    >
      <div className="relative aspect-[16/9] bg-blue-500/10">
        <Image
          src={getOnDemandImage(city)}
          alt={t('FICHA.ON_DEMAND.EYEBROW')}
          fill
          sizes="(max-width: 900px) 100vw, 400px"
          className="object-cover object-[center_30%]"
          loading="lazy"
        />
      </div>
      <div className="p-5 md:p-6">
        <span className="font-display text-[10px] tracking-[0.16em] text-blue-500 uppercase md:text-[11px]">
          {t('FICHA.ON_DEMAND.EYEBROW')}
        </span>
        <h2 className="mt-1.5 font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
          {t('FICHA.ON_DEMAND.TITLE', { city: config.displayShortName })}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {t('FICHA.ON_DEMAND.BODY')}
        </p>
        <p className="mt-2 font-display text-[14px] font-medium text-blue-500 italic md:text-[15px]">
          {t('FICHA.ON_DEMAND.PRICE_FROM', {
            price: numberWithCommas(config.perSqmRates.twelveMonths)
          })}
        </p>
        <Link
          href={buildOnDemandHref(city)}
          onClick={() =>
            onDemandCardClick({ city, surface: 'directory_ficha' })
          }
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-5 py-2.5 text-sm font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5"
        >
          {t('FICHA.ON_DEMAND.CTA')}
          <i className="si si-right text-[9px]" aria-hidden="true" />
        </Link>
      </div>
    </section>
  )
}

export default React.memo(OnDemandFichaCard)
